import { DeleteCellRenderer } from '@/components/grid';
import { ListPageLayout } from '@/components/layout/list-page-layout';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { extractCheckResultError } from '@/lib/checkResult';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import type { ColDef } from 'ag-grid-community';
import { Play } from 'lucide-react';
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ScenarioDetails } from './scenario-details';
import {
	selectScenarioAfterSave,
	type ScenarioListRecord
} from './scenario-list-selection';
import { SCENARIO_DELETE_MUTATION, SCENARIO_QUERY, SCENARIOS_LIST_QUERY } from './scenarios.gql.tsx';
import { cloneScenario } from './cloneScenario';

interface Scenario {
	scenarioId: string;
	name: string;
}

export interface ScenariosListRef {
	selectScenarioAfterSave: (scenarioId: string) => Promise<void>;
}

const ScenarioEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<Play className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export const ScenariosList = forwardRef<ScenariosListRef>((_, ref) => {
	const { t } = useTranslation('scenarios');
	const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(
		null
	);
	const [activeDetailsTab, setActiveDetailsTab] = useState('details');
	const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
	const [localScenarios, setLocalScenarios] = useState<ScenarioListRecord[]>(
		[]
	);
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);
	const { showDialog } = useConfirmationDialog();
	const apolloClient = useApolloClient();
	const location = useLocation();
	const navigate = useNavigate();

	const {
		data: queryData,
		loading: isLoading,
		fetchMore,
		refetch
	} = useQuery(SCENARIOS_LIST_QUERY, {
		variables: { first: itemsToFetch },
		fetchPolicy: 'cache-and-network'
	});

	const scenariosData = queryData?.scenarios?.nodes || [];
	const pageInfo = queryData?.scenarios?.pageInfo;
	const hasNextPage = pageInfo?.hasNextPage ?? false;
	const endCursor = pageInfo?.endCursor;

	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) return prev;
					const newNodes = fetchMoreResult.scenarios?.nodes || [];
					const prevNodes = prev.scenarios?.nodes || [];
					setItemsToFetch(prevNodes.length + newNodes.length);
					setLocalScenarios([]);
					return {
						...prev,
						scenarios: {
							...fetchMoreResult.scenarios,
							nodes: [...prevNodes, ...newNodes]
						}
					};
				}
			});
		} finally {
			setIsLoadingMore(false);
		}
	}, [hasNextPage, isLoadingMore, endCursor, fetchMore]);

	const goToCreate = useCallback(() => {
		navigate('/scenarios/create');
	}, [navigate]);

	const goToEdit = useCallback(
		(id: string) => {
			navigate(`/scenarios/edit/${id}`);
		},
		[navigate]
	);

	const refresh = useCallback(() => {
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		setLocalScenarios([]);
		refetch({ first: DEFAULT_PAGE_SIZE });
	}, [refetch]);

	// Delete mutation
	const [deleteScenario, { loading: deleteLoading }] = useMutation(
		SCENARIO_DELETE_MUTATION
	);

	// Use local scenarios data if available, otherwise fall back to server data
	const scenarios = useMemo(() => {
		const serverScenarios = scenariosData || [];
		// Always prefer server data - local state is only for optimistic updates
		if (localScenarios.length === 0 || serverScenarios.length === 0) {
			return serverScenarios;
		}
		return localScenarios;
	}, [scenariosData, localScenarios]);

	// Expose method to select scenario after save via ref
	useImperativeHandle(
		ref,
		() => ({
			selectScenarioAfterSave: async (scenarioId: string) => {
				await selectScenarioAfterSave({
					scenarioId,
					client: apolloClient,
					currentScenarios: scenarios,
					onDataUpdate: setLocalScenarios,
					onScenarioSelect: setSelectedScenario,
					onOpenDetailsPanel: () => setDetailsPanelOpen(true)
				});
			}
		}),
		[apolloClient, scenarios]
	);

	// Handle scenario selection from location state (after create/edit)
	useEffect(() => {
		const state = location.state as { selectScenarioId?: string } | null;
		if (state?.selectScenarioId && scenarios.length > 0 && !isLoading) {
			const scenarioIdToSelect = state.selectScenarioId;
			// Trigger the selection after a short delay to ensure grid is ready
			const timeoutId = setTimeout(async () => {
				try {
					await selectScenarioAfterSave({
						scenarioId: scenarioIdToSelect,
						client: apolloClient,
						currentScenarios: scenarios,
						onDataUpdate: setLocalScenarios,
						onScenarioSelect: setSelectedScenario,
						onOpenDetailsPanel: () => setDetailsPanelOpen(true)
					});
					// Clear the location state after processing to prevent re-triggering
					navigate(location.pathname, { replace: true, state: null });
				} catch (error) {
					console.error(
						'Failed to select scenario after navigation:',
						error
					);
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [
		location.state,
		scenarios.length,
		isLoading,
		apolloClient,
		scenarios,
		navigate,
		location.pathname
	]);

	const handleRowClick = (scenario: Scenario) => {
		setSelectedScenario(scenario);
		setDetailsPanelOpen(true);
	};

	const handleCloseDetails = () => {
		setDetailsPanelOpen(false);
		setSelectedScenario(null);
	};

	// Helper function to find the next scenario to select after deletion
	const findNextScenarioToSelect = (
		deletedScenario: Scenario,
		scenarioList: Scenario[]
	) => {
		const currentIndex = scenarioList.findIndex(
			s => s.scenarioId === deletedScenario.scenarioId
		);

		if (scenarioList.length <= 1) {
			return null;
		}

		// Try to select the next scenario (same index after deletion)
		if (currentIndex < scenarioList.length - 1) {
			return scenarioList[currentIndex + 1];
		}

		// If deleting the last scenario, select the previous one
		if (currentIndex > 0) {
			return scenarioList[currentIndex - 1];
		}

		return null;
	};

	// Helper function to handle post-deletion selection
	const handlePostDeletionSelection = (
		nextScenarioToSelect: Scenario | null,
		updatedScenarios: Scenario[]
	) => {
		setLocalScenarios([]);
		// First, clear the current selection completely
		setSelectedScenario(null);

		// Then after a brief delay, set the next scenario
		setTimeout(() => {
			if (nextScenarioToSelect && updatedScenarios.length > 0) {
				// Find the scenario in the updated list by ID
				const updatedScenario = updatedScenarios.find(
					s => s.scenarioId === nextScenarioToSelect?.scenarioId
				);

				if (updatedScenario) {
					setSelectedScenario(updatedScenario);
				}
			}
		}, 100);
	};

	const handleDeleteScenario = async (scenario: Scenario) => {
		const confirmed = await showDialog(
			'Delete Scenario',
			`Are you sure you want to delete scenario "${scenario.name}"? This action cannot be undone.`
		);

		if (confirmed) {
			try {
				// Find the next scenario to select after deletion (before deletion happens)
				const nextScenarioToSelect = findNextScenarioToSelect(
					scenario,
					scenarios
				);

				const result = await deleteScenario({
					variables: {
						input: {
							scenarioId: scenario.scenarioId
						}
					},
					refetchQueries: ['Scenarios'],
					awaitRefetchQueries: true
				});

				const checkResult = result.data?.deleteScenario;

				if (checkResult?.success) {
					showMutationSuccessToast('scenarios', 'delete');

					// After refetch completes, handle selection
					// The scenariosData should now be updated
					handlePostDeletionSelection(
						nextScenarioToSelect,
						scenariosData || []
					);
				} else {
					const errorMessage = extractCheckResultError(checkResult);
					toast.error(`Failed to delete scenario: ${errorMessage}`);
				}
			} catch (error) {
				console.error('Delete scenario error:', error);
				toast.error('Failed to delete scenario. Please try again.');
			}
		}
	};

	const handleDeleteScenarioById = async (scenarioId: string) => {
		const scenario = scenarios?.find((s: Scenario) => s.scenarioId === scenarioId);
		if (scenario) {
			await handleDeleteScenario(scenario);
		}
	};

	const handleCloneScenario = async (scenarioId: string) => {
		try {
			// Fetch full scenario details for cloning (including conditions)
			const { data } = await apolloClient.query({
				query: SCENARIO_QUERY,
				variables: { id: scenarioId },
				fetchPolicy: 'network-only'
			});

			const scenario = data?.scenario;
			if (!scenario) {
				toast.error(t('messages.cloneError'));
				return;
			}

			// Use the cloneScenario function for proper ID and condition handling
			const cloneData = cloneScenario({
				name: scenario.name,
				scenarioConditions: scenario.scenarioConditions
			});

			navigate('/scenarios/create', {
				state: { cloneData }
			});
		} catch (error) {
			console.error('Failed to clone scenario:', error);
			toast.error(t('messages.cloneError'));
		}
	};

	// Define AG Grid columns
	const columns: ColDef<Scenario>[] = [
		{
			headerName: t('list.columns.name'),
			field: 'name',
			flex: 1
		},
		{
			headerName: '',
			field: undefined,
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			cellRenderer: DeleteCellRenderer,
			cellRendererParams: {
				onDelete: handleDeleteScenario,
				disabled: deleteLoading
			},
			sortable: false,
			filter: false,
			resizable: false
		}
	];

	return (
		<ListPageLayout<Scenario>
			columns={columns}
			data={scenarios || []}
			title={t('list.title')}
			description={t('list.description')}
			searchPlaceholder={t('list.searchPlaceholder')}
			isLoading={isLoading || deleteLoading}
			onRowClick={handleRowClick}
			onCreate={goToCreate}
			onRefresh={() => {
				setLocalScenarios([]);
				refresh();
			}}
			detailsPanelOpen={detailsPanelOpen}
			onCloseDetails={handleCloseDetails}
			selectedItem={selectedScenario}
			detailsTitle="Scenario Details"
			onEdit={goToEdit}
			onDelete={handleDeleteScenarioById}
			onClone={handleCloneScenario}
			onOpenInNewWindow={id => openEntityInNewWindow('scenarios', id)}
			getRecordId={item => item.scenarioId}
			getDetailsDescription={item => item.name}
			renderDetailsContent={(scenario: Scenario) => (
				<ScenarioDetails
					scenarioId={scenario.scenarioId}
					activeTab={activeDetailsTab}
					onTabChange={setActiveDetailsTab}
					hideTabsList={true}
				/>
			)}
			renderEntityIcon={() => <ScenarioEntityIcon />}
			getEntityTypeName={() => t('details.scenario')}
			showCreateButton={true}
			showRefreshButton={true}
			hasNextPage={hasNextPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			entityName={t('entityName')}
		/>
	);
});

ScenariosList.displayName = 'ScenariosList';
